import { useState, useEffect } from 'react';
import { 
  IndianRupee, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import MetricCard from './MetricCard';

function MetricsGrid({ metrics, loading }) {
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Set up auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      // Trigger refresh callback if provided
      if (typeof window !== 'undefined' && window.refreshBudgetData) {
        window.refreshBudgetData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    setRefreshInterval(interval);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  // Calculate utilization status
  const getUtilizationStatus = (utilizationRate) => {
    if (utilizationRate >= 90) return 'critical';
    if (utilizationRate >= 75) return 'warning';
    return 'healthy';
  };

  const utilizationStatus = getUtilizationStatus(metrics.utilizationRate);

  // Format currency values
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${Math.round(value)}%`;
  };

  const metricsData = [
    {
      id: 'total-budget',
      title: 'Total Budget',
      value: formatCurrency(metrics.totalBudget),
      icon: <IndianRupee className="w-6 h-6" />,
      status: 'neutral',
      trend: null,
      description: 'Total allocated budget for current period'
    },
    {
      id: 'total-spent',
      title: 'Total Spent',
      value: formatCurrency(metrics.totalSpent),
      icon: <TrendingDown className="w-6 h-6" />,
      status: 'neutral',
      trend: null,
      description: 'Total amount spent to date'
    },
    {
      id: 'remaining-budget',
      title: 'Remaining Budget',
      value: formatCurrency(metrics.remainingBudget),
      icon: <TrendingUp className="w-6 h-6" />,
      status: metrics.remainingBudget > 0 ? 'healthy' : 'critical',
      trend: null,
      description: 'Available budget remaining'
    },
    {
      id: 'utilization-rate',
      title: 'Budget Utilization',
      value: formatPercentage(metrics.utilizationRate),
      icon: utilizationStatus === 'critical' ? <AlertTriangle className="w-6 h-6" /> :
            utilizationStatus === 'warning' ? <Clock className="w-6 h-6" /> :
            <CheckCircle className="w-6 h-6" />,
      status: utilizationStatus,
      trend: null,
      description: 'Percentage of budget utilized'
    }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div 
            key={i}
            className="rounded-xl p-6 border animate-pulse"
            style={{ 
              background: 'var(--panel)', 
              borderColor: 'var(--border)' 
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div 
                className="w-12 h-6 rounded"
                style={{ background: 'var(--border)' }}
              />
              <div 
                className="w-6 h-6 rounded"
                style={{ background: 'var(--border)' }}
              />
            </div>
            <div 
              className="w-20 h-8 rounded mb-2"
              style={{ background: 'var(--border)' }}
            />
            <div 
              className="w-full h-4 rounded"
              style={{ background: 'var(--border)' }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--text)' }}>
          Key Metrics
        </h2>
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span>Auto-refresh every 5 minutes</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricsData.map((metric) => (
          <MetricCard
            key={metric.id}
            title={metric.title}
            value={metric.value}
            icon={metric.icon}
            status={metric.status}
            trend={metric.trend}
            description={metric.description}
          />
        ))}
      </div>

      {/* Status Summary */}
      <div 
        className="rounded-xl p-4 border"
        style={{ 
          background: 'var(--panel)', 
          borderColor: 'var(--border)' 
        }}
      >
        <div className="flex items-center gap-3">
          {utilizationStatus === 'critical' && (
            <>
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-sm font-medium text-red-600">
                Critical: Budget utilization is at {formatPercentage(metrics.utilizationRate)}
              </span>
            </>
          )}
          {utilizationStatus === 'warning' && (
            <>
              <Clock className="w-5 h-5 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-600">
                Warning: Budget utilization is at {formatPercentage(metrics.utilizationRate)}
              </span>
            </>
          )}
          {utilizationStatus === 'healthy' && (
            <>
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-green-600">
                Healthy: Budget utilization is at {formatPercentage(metrics.utilizationRate)}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MetricsGrid;